import { isDefAndNotNull } from '@utils/def';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Sizes as AutoCompleteSizes,
  OptionType,
} from '../../../../../AutoComplete';
import Input from '../../../../../Input';
import { MeasuringUnit } from '../../../../enums';
import {
  MEASURING_UNIT_LABEL,
  convertMeasuringUnit,
} from '../../../../measuringUnits';

import { StyledUnitLabel, StyledUnitPicker } from './styles';
import { Props } from './types';

const MeasuringUnitInput: FC<Props> = ({
  measuringUnit,
  onChange,
  value,
  ...rest
}) => {
  const [unit, setUnit] = useState(measuringUnit.originalUnit);
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    setUnit(measuringUnit.originalUnit);
    setDraft(null);
  }, [measuringUnit.originalUnit]);

  const displayValue = useMemo(() => {
    if (draft !== null) return draft;
    if (!isDefAndNotNull(value) || value === '') return value ?? '';

    return convertMeasuringUnit(
      Number(value),
      measuringUnit.originalUnit,
      unit,
    );
  }, [draft, measuringUnit.originalUnit, unit, value]);

  const handleChange = useCallback(
    (nextValue: string) => {
      const isIncomplete =
        nextValue === '-' || /\.$/.test(nextValue) || nextValue === '';

      if (isIncomplete) {
        setDraft(nextValue);
        if (nextValue === '') onChange?.('');
        return;
      }

      setDraft(null);

      const numeric = Number(nextValue);
      onChange?.(
        Number.isNaN(numeric)
          ? nextValue
          : String(
              convertMeasuringUnit(numeric, unit, measuringUnit.originalUnit),
            ),
      );
    },
    [measuringUnit.originalUnit, onChange, unit],
  );

  const unitOptions: OptionType[] = useMemo(
    () =>
      (measuringUnit.availableUnits ?? []).map((option) => ({
        value: option,
        label: MEASURING_UNIT_LABEL[option],
      })),
    [measuringUnit.availableUnits],
  );

  const handleUnitChange = useCallback(
    (option: OptionType | OptionType[] | null) => {
      if (!option || Array.isArray(option)) return;

      setDraft(null);
      setUnit(option.value as MeasuringUnit);
    },
    [],
  );

  return (
    <Input
      {...rest}
      value={displayValue}
      onChange={handleChange}
      postfix={
        unitOptions.length > 1 ? (
          <StyledUnitPicker
            name={`${rest.name}-unit`}
            options={unitOptions}
            value={unit}
            onChange={handleUnitChange}
            disableClearable
            size={AutoCompleteSizes.small}
          />
        ) : (
          <StyledUnitLabel>
            {MEASURING_UNIT_LABEL[measuringUnit.originalUnit]}
          </StyledUnitLabel>
        )
      }
    />
  );
};

export default MeasuringUnitInput;
